import { ErrorModal, MesfModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid, IconButton, Typography } from "@mui/material";
import { PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import {
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  IStatusPanelParams,
  MenuItemDef,
  RowSelectedEvent,
} from "ag-grid-community";
import { deleteLocation } from "@/pages/billets/repositories/LocationRepository";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { Location } from "../../../models/CatLocation";
import { useLocationOptionsFunctions } from "../hooks/useLocationOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { LocationDetails } from "./LocationDetails";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";

const AddLocationStatusPanel = ({
  handleNewLocation,
}: {
  handleNewLocation: () => void;
}) => {
  return (
    <IconButton
      onClick={handleNewLocation}
      color="primary"
      style={{
        padding: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      <PlaylistAddIcon style={{ width: 30, height: 30 }} />
    </IconButton>
  );
};

type Props = {
  isLoading: boolean;
  getLocationsFromAPI: Function;
  locationId: string | undefined;
};

export const TableLocation = ({
  isLoading,
  getLocationsFromAPI,
  locationId,
}: Props) => {
  const navigate = useNavigate();

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [location, setLocation] = useState<Location | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [error, setError] = useState("");

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const handleNewLocation = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setLocation(null);
    navigate(`/configuration/byi-locations/new`);
  };

  // const { getMenuOptions } = useLocationOptionsFunctions({
  //   setLocation,
  //   setShowModal,
  //   handleNewLocation,
  // });

  const { rows, columnDefs } = useTableData({
    setLocation,
    setShowModal,
    showContextMenu,
  });

  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: Location) => {
      setShowModal("edit");
      setLocation(data);
    },
  });

  const onRowSelected = (event: RowSelectedEvent) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setLocation(event.data);
      setShowModal("edit");
      navigate(
        `/configuration/byi-locations/${(event.data as Location).LocationId}`,
      );
    }
  };

  const onModalHide = ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => {
    if (close) {
      setShowModal("");
      setLocation(null);
      if (gridApi) gridApi.deselectAll();
    }
    if (shouldUpdate) {
      setTimeout(() => {
        const rowNode = gridApi?.getRowNode(String(entityId));
        if (rowNode) {
          gridApi?.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
          rowNode?.setSelected(true);
          setLocation(rowNode?.data);
        }
      }, 0);
    }
    navigate(`/configuration/byi-locations`);
  };

  useEffect(() => {
    if (isLoading) {
      gridApi?.showLoadingOverlay();
    } else {
      gridApi?.hideOverlay();
      if (locationId) {
        if (locationId === "new") {
          handleNewLocation();
        } else {
          let locationNode: IRowNode<Location>;

          gridApi?.forEachNode((n) => {
            if ((n.data as Location).LocationId === Number(locationId)) {
              locationNode = n;
              return true;
            }
          });

          if (locationNode!) {
            gridApi?.ensureIndexVisible(locationNode.rowIndex ?? 0, "middle");
            locationNode.setSelected(true);
            setLocation(locationNode.data || null);
            setShowModal("edit");
          }
        }
      }
    }
  }, [isLoading, gridApi]);

  // useEffect(() => {
  //   registerConfig({
  //     id: "locationContext",
  //     getOptions: getMenuOptions,
  //   });
  // }, []);

  const handleDeleteLocation = async () => {
    try {
      const resp = await deleteLocation(location?.LocationId!);

      if (!resp.ok) {
        setError(resp.message);
        return;
      }
      setShowModal("");
      setLocation(null);
      if (gridApi) gridApi.deselectAll();
      await getLocationsFromAPI();
      navigate(`/configuration/byi-locations`);
    } catch (err) {
      setError(err as string);
    }
  };

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Locations
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          style={{ width: "100%", padding: "0 10px" }}
          spacing={2}
        >
          <Grid item md={4} xs={12}>
            <div style={{ height: "72vh", width: "100%" }}>
              <AgGridReact<Location>
                rowData={rows || []}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                {...defaultGridOptions}
                // getContextMenuItems={getContextMenuItems}
                onGridReady={(params) => setGridApi(params.api)}
                getRowId={(params) => String(params.data.LocationId)}
                loadingOverlayComponent={CenteredLazyLoading}
                onRowSelected={onRowSelected}
                gridOptions={{
                  statusBar: {
                    statusPanels: [
                      {
                        statusPanel: "agTotalRowCountComponent",
                        align: "right",
                      },
                      // {
                      //   statusPanel: (params: IStatusPanelParams) => (
                      //     <AddLocationStatusPanel
                      //       handleNewLocation={handleNewLocation}
                      //     />
                      //   ),
                      //   align: "left",
                      // },
                    ],
                  },
                }}
              />
            </div>
          </Grid>
          <Grid item md={8} xs={12}>
            <div style={{ height: "72vh", width: "100%" }}>
              <LocationDetails
                location={location}
                modal={showModal}
                refreshData={getLocationsFromAPI}
                onHide={onModalHide}
              />
            </div>
          </Grid>
        </Grid>
      </Grid>
      <MesfModal
        title={`DELETE LOCATION`}
        open={showModal === "delete"}
        handleClose={() => setShowModal("")}
        maxWidth="md"
      >
        <MesfModal.Content style={{ padding: "15px 30px" }}>
          {isLoading && <CenteredLazyLoading />}
          {!isLoading && (
            <>
              <Grid container spacing={1}>
                <Grid item md={12} xs={12}>
                  <Typography>
                    Are you sure you want to delete{" "}
                    <strong>{location?.Name}</strong> location?
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </MesfModal.Content>
        <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => setShowModal("")}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleDeleteLocation}
              >
                DELETE
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
